import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/api";

export default function Lightbox({ photo, onClose, onPrev, onNext, showBookCta = true }) {
  return (
    <motion.div
      className="fixed inset-0 z-50 bg-ink/95 backdrop-blur-md flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="photo-lightbox"
    >
      <div className="flex items-center justify-between px-4 sm:px-8 h-16 border-b border-white/10 shrink-0">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
          {photo.category === "automotive" ? "Automotive" : "Personal / Portrait"} — {photo.subcategory}
        </p>
        <button
          data-testid="lightbox-close"
          onClick={onClose}
          className="text-ash hover:text-bone transition-colors"
          aria-label="Close"
        >
          <X size={22} />
        </button>
      </div>
      <div className="flex-1 grid lg:grid-cols-[1fr_320px] min-h-0">
        <div className="relative flex items-center justify-center p-4 sm:p-10 min-h-[42vh] lg:min-h-0">
          <motion.img
            key={photo.id}
            src={imgUrl(photo.src)}
            alt={photo.title}
            data-testid="lightbox-image"
            className="max-h-full max-w-full object-contain border border-white/10"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          />
          <button
            data-testid="lightbox-prev"
            onClick={onPrev}
            aria-label="Previous photo"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-3 border border-white/15 bg-ink/60 text-ash hover:text-bone hover:border-flame transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="lightbox-next"
            onClick={onNext}
            aria-label="Next photo"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-3 border border-white/15 bg-ink/60 text-ash hover:text-bone hover:border-flame transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <aside className="border-t lg:border-t-0 lg:border-l border-white/10 p-6 sm:p-8 flex flex-col gap-6 overflow-y-auto">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-2">{photo.subcategory}</p>
            <h3 className="font-serif text-3xl text-bone" data-testid="lightbox-title">{photo.title}</h3>
          </div>
          <div className="space-y-3 font-mono text-xs text-ash">
            {photo.specs && (
              <div className="flex justify-between gap-4 border-b border-white/5 pb-2">
                <span className="text-faint uppercase tracking-widest">Capture</span>
                <span data-testid="lightbox-specs" className="text-right">{photo.specs}</span>
              </div>
            )}
            <div className="flex justify-between gap-4 border-b border-white/5 pb-2">
              <span className="text-faint uppercase tracking-widest">Frame</span>
              <span>{photo.width} × {photo.height}</span>
            </div>
          </div>
          {showBookCta && (
            <Link
              data-testid="lightbox-book-cta"
              to={`/book?session=${photo.category}&photo=${photo.id}`}
              className="group mt-auto flex items-center justify-between border border-flame/60 px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] text-bone hover:bg-flame hover:text-ink transition-colors duration-300"
            >
              Book this session style
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:rotate-45" />
            </Link>
          )}
        </aside>
      </div>
    </motion.div>
  );
}
